import React from 'react';
import { CockpitActionsTab } from './cockpit-actions.jsx';
import { COCKPIT_ALERTS, COCKPIT_TENANT, getCockpitDataForStore } from './cockpit-data.jsx';
import { CockpitDomainsTab } from './cockpit-domains.jsx';
import { CockpitHealthTab } from './cockpit-health.jsx';
import { CockpitLogsTab } from './cockpit-logs.jsx';
import { CockpitProvisionTab } from './cockpit-provision.jsx';
import { CockpitUsersTab } from './cockpit-users.jsx';
import { Dashboard } from './dashboard.jsx';
import { daysBetween, fmtDate, makeDate } from './data.jsx';
import { TENANT_STATUS_CONFIG } from './extended-mock-data.jsx';
import { Icon } from './icons.jsx';
import { normStatus } from './lojas.jsx';
import { FEATURE_IMPERSONATION, saIsRealRepairsMode } from './mvp-config.jsx';
import { auditService, billingService, formatAuditTimestamp, repairService, tenantService } from './services.jsx';
import { OperationalStatusBadge, TenantStatusBadge } from './tenant-status.jsx';
// Tenant Cockpit — shell principal + Tab Visão Geral

// TenantStatusBadge: definida em tenant-status.jsx (carrega após este arquivo).
// Fallback inline para uso dentro do cockpit antes do override.
const _CockpitStatusBadge = ({ status }) => {
  const cfg = TENANT_STATUS_CONFIG || {};
  const c = cfg[status] || { label: status, color: 'var(--text-muted)', bg: 'var(--bg-subtle)', dot: 'var(--text-muted)' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 12, fontWeight: 700 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot || c.color }} />
      {c.label}
    </span>
  );
};
// Use TenantStatusBadge se disponível (vem de tenant-status.jsx), senão fallback
const _renderStatusBadge = (status) => {
  const Badge = TenantStatusBadge || _CockpitStatusBadge;
  return React.createElement(Badge, { status });
};

// ── TenantCockpitHeader ───────────────────────────────────────────────────────
const TenantCockpitHeader = ({ store, cockpitData, onImpersonate, onChangePlan, onSuspend, onToast, logAction }) => {
  const showImpersonation = FEATURE_IMPERSONATION === true;
  const [moreOpen, setMoreOpen] = React.useState(false);
  // Usa cockpitData (preparado pelo shell) ou fallback direto do store
  const t = cockpitData || COCKPIT_TENANT || store;
  const ns = (typeof normStatus !== 'undefined') ? normStatus(store.status || t.status) : (store.status || t.status);

  React.useEffect(() => {
    if (!moreOpen) return;
    const h = () => setMoreOpen(false);
    setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => document.removeEventListener('mousedown', h);
  }, [moreOpen]);

  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '20px 24px', marginBottom: 24, boxShadow: 'var(--shadow-sm)' }}>
      {/* Top row */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Avatar */}
        <div style={{ width: 56, height: 56, borderRadius: 14, background: store.color || t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: 'white', flexShrink: 0, letterSpacing: '-0.02em' }}>
          {t.name.split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
        </div>
        {/* Meta */}
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>{t.name}</h1>
            {_renderStatusBadge(ns)}
            {(store.operational_status && store.operational_status !== 'active' && typeof OperationalStatusBadge !== 'undefined') && (
              <OperationalStatusBadge status={store.operational_status} label={store.operational_status_label} size="sm" />
            )}
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', background: 'var(--brand-navy)', color: 'white', borderRadius: 20 }}>{(t.plan || {}).name || '—'}</span>
          </div>
          <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
            {[
              { label: 'ID',     value: `ten_${String(t.id).padStart(4,'0')}`, mono: true },
              { label: 'Schema', value: t.schemaName, mono: true },
              { label: 'Domínio', value: t.domain, mono: true },
            ].map((item, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ margin: '0 10px', color: 'var(--border-strong)' }}>·</span>}
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  <span style={{ fontWeight: 500, marginRight: 4 }}>{item.label}:</span>
                  <span style={{ fontFamily: item.mono ? 'JetBrains Mono, monospace' : 'inherit', color: 'var(--text-secondary)' }}>{item.value}</span>
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => { window.open('https://' + t.domain, '_blank'); onToast('Dashboard aberto em nova aba'); }}>
            <Icon name="externalLink" size={14} />Dashboard
          </button>
          {showImpersonation && (
            <button className="btn" onClick={onImpersonate} disabled={ns === 'suspended' || ns === 'suspenso'}>
              <Icon name="switchUser" size={14} />Login como lojista
            </button>
          )}
          <button className="btn" onClick={onChangePlan}>
            <Icon name="edit" size={14} />Alterar plano
          </button>
          <button className="btn danger" onClick={onSuspend}>
            <Icon name={(ns === 'suspended' || ns === 'suspenso') ? 'play' : 'suspend'} size={14} />
            {(ns === 'suspended' || ns === 'suspenso') ? 'Reativar' : 'Suspender'}
          </button>
          {/* More menu */}
          <div style={{ position: 'relative' }}>
            <button className="btn" onClick={e => { e.stopPropagation(); setMoreOpen(o => !o); }}>
              <Icon name="more" size={14} />Mais
            </button>
            {moreOpen && (
              <div onMouseDown={e => e.stopPropagation()} style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 220, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', zIndex: 200, overflow: 'hidden' }}>
                {[
                  { label: 'Resetar senha do owner', icon: 'key',     action: () => onToast('E-mail de redefinição enviado') },
                  { label: 'Forçar logout de todos', icon: 'lock',    action: () => onToast('Todas as sessões encerradas') },
                  { label: 'Revogar tokens',          icon: 'shield',  action: () => onToast('Tokens revogados') },
                  { label: 'Limpar cache',            icon: 'refresh', action: () => onToast('Cache do tenant limpo') },
                  { label: 'Rodar diagnóstico',       icon: 'activity',action: () => onToast('Diagnóstico iniciado…') },
                ].map((item, i) => (
                  <button key={i} onClick={() => { item.action(); setMoreOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text)', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <Icon name={item.icon} size={14} style={{ color: 'var(--text-muted)' }} />{item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── CockpitOverviewTab ────────────────────────────────────────────────────────
const CockpitOverviewTab = ({ cockpitData, onToast, onSwitchTab, onImpersonate, onChangePlan }) => {
  // Usa cockpitData passado pelo shell (já reflete o tenant selecionado)
  const t = cockpitData || COCKPIT_TENANT;
  const daysSince  = t.createdAt instanceof Date ? daysBetween(t.createdAt, makeDate(0)) : '—';
  const storagePct = Math.round(((t.storageUsed || 0) / (t.storageLimit || 10)) * 100);
  const planPrice  = (t.plan && t.plan.price) ? `R$ ${t.plan.price.toFixed(2).replace('.', ',')}/mês` : 'Trial';

  // Cor do health score
  const hsColor = t.healthScore >= 80 ? 'var(--success)' : t.healthScore >= 50 ? 'var(--warning)' : 'var(--danger)';
  const hsLabel  = t.healthScore >= 80 ? 'Saudável' : t.healthScore >= 50 ? 'Atenção' : 'Crítico';

  // Billing status
  const billingMap = {
    active:   { label: 'Em dia',        color: 'var(--success)' },
    past_due: { label: 'Inadimplente',  color: 'var(--danger)'  },
    unpaid:   { label: 'Suspenso',      color: 'var(--danger)'  },
    trialing: { label: 'Trial',         color: 'var(--info)'    },
    unknown:  { label: '—',             color: 'var(--text-muted)' },
  };
  const billingInfo = billingMap[t.billingStatus] || billingMap.unknown;

  const kpis = [
    {
      label: 'Status operacional',
      value: (store.operational_status && typeof OperationalStatusBadge !== 'undefined')
        ? <OperationalStatusBadge status={store.operational_status || 'active'} label={store.operational_status_label} size="sm" />
        : _renderStatusBadge(store.operational_status || 'active'),
      icon: 'activity',
    },
    { label: 'Plano atual',        value: `${(t.plan || {}).name || '—'} · ${planPrice}`, icon: 'plans' },
    { label: 'Dias no ar',         value: `${daysSince}d`,              icon: 'clock'  },
    { label: 'Owner',              value: t.ownerName || '—',           icon: 'users'  },
    { label: 'Usuários staff',     value: t.staffCount || 0,            icon: 'users'  },
    { label: 'Schema',             value: t.schemaName || '—',          icon: 'server' },
  ];

  return (
    <div>
      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        {kpis.map((k, i) => (
          <div key={i} className="kpi">
            <div className="label"><Icon name={k.icon} size={14} />{k.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8, color: 'var(--text)' }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
        {/* Health score mini */}
        <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => onSwitchTab('health')}>
          <div className="label"><Icon name="activity" size={14} />Saúde do tenant</div>
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 26, fontWeight: 700, color: hsColor, letterSpacing: '-0.02em' }}>{t.healthScore}</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>/100</span>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: hsColor + '22', color: hsColor, marginLeft: 4 }}>{hsLabel}</span>
            </div>
            <div style={{ height: 5, background: 'var(--bg-subtle)', borderRadius: 3 }}>
              <div style={{ height: '100%', width: (t.healthScore || 0) + '%', background: hsColor, borderRadius: 3 }} />
            </div>
          </div>
        </div>
        {/* Storage */}
        <div className="kpi">
          <div className="label"><Icon name="server" size={14} />Storage</div>
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
              <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{t.storageUsed || 0}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>/ {t.storageLimit || 10} GB</span>
            </div>
            <div style={{ height: 5, background: 'var(--bg-subtle)', borderRadius: 3 }}>
              <div style={{ height: '100%', width: storagePct + '%', background: 'var(--brand-navy)', borderRadius: 3 }} />
            </div>
          </div>
        </div>
        {/* Billing */}
        <div className="kpi">
          <div className="label"><Icon name="plans" size={14} />Billing</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8, color: billingInfo.color }}>{billingInfo.label}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
            {t.billingStatus === 'trialing' ? `Trial até ${t.trial_ends_at ? t.trial_ends_at.split('T')[0] : '—'}` :
             t.billingStatus === 'past_due'  ? `Inadimplente · intervenção necessária` :
             t.billingStatus === 'unpaid'    ? `Suspenso por inadimplência` :
             'Próxima cobrança no próximo ciclo'}
          </div>
        </div>
      </div>

      {/* Alerts — lê do global COCKPIT_ALERTS (atualizado por getCockpitDataForStore) */}
      {(cockpitData?.alerts || COCKPIT_ALERTS || []).length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-head">
            <Icon name="alert" size={15} style={{ color: 'var(--warning)' }} />
            <h3>Alertas do tenant</h3>
            <span className="sub">{(cockpitData?.alerts || COCKPIT_ALERTS || []).length} alertas ativos</span>
          </div>
          <div>
            {(cockpitData?.alerts || COCKPIT_ALERTS || []).map((alert, i) => {
              const isLast = i === (cockpitData?.alerts || COCKPIT_ALERTS || []).length - 1;
              const color  = alert.severity === 'danger' ? 'var(--danger)' : 'var(--warning)';
              const bg     = alert.severity === 'danger' ? 'var(--danger-bg)' : 'var(--warning-bg)';
              return (
                <div key={alert.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: isLast ? 'none' : '1px solid var(--border)' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
                    <Icon name="alert" size={17} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{alert.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{alert.desc}</div>
                  </div>
                  <button className="btn sm" style={{ flexShrink: 0, background: bg, color, border: 'none', fontWeight: 600 }} onClick={() => onSwitchTab(alert.tab)}>
                    {alert.action}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Owner info */}
        <div className="card">
          <div className="card-head"><Icon name="users" size={15} style={{ color: 'var(--text-muted)' }} /><h3>Owner</h3></div>
          <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Nome',   value: t.ownerName  || '—' },
              { label: 'E-mail', value: t.ownerEmail || '—', mono: true },
              { label: 'Fone',   value: t.ownerPhone || '—' },
              { label: 'Criado', value: t.createdAt instanceof Date ? fmtDate(t.createdAt) : (t.createdAt || '—') },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, paddingBottom: 10, borderBottom: i < 3 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{r.label}</span>
                <span style={{ color: 'var(--text)', fontFamily: r.mono ? 'JetBrains Mono, monospace' : 'inherit', fontSize: r.mono ? 12 : 13 }}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Infra */}
        <div className="card">
          <div className="card-head"><Icon name="server" size={15} style={{ color: 'var(--text-muted)' }} /><h3>Infraestrutura</h3></div>
          <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Schema',        value: t.schemaName || '—', mono: true },
              { label: 'Domínio',       value: t.domain || '—', mono: true },
              { label: 'Storage usado', value: `${t.storageUsed || 0} / ${t.storageLimit || 10} GB` },
              { label: 'Billing',       value: billingInfo.label, ok: t.billingStatus === 'active', warn: t.billingStatus === 'trialing', danger: ['past_due','unpaid'].includes(t.billingStatus) },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, paddingBottom: 10, borderBottom: i < 3 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{r.label}</span>
                <span style={{
                  color: r.ok ? 'var(--success)' : r.warn ? 'var(--info)' : r.danger ? 'var(--danger)' : 'var(--text)',
                  fontFamily: r.mono ? 'JetBrains Mono, monospace' : 'inherit',
                  fontSize: r.mono ? 12 : 13,
                  fontWeight: (r.ok || r.warn || r.danger) ? 600 : 400,
                }}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── TenantCockpit (shell) ─────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',     label: 'Visão geral'           },
  { id: 'health',       label: 'Saúde do tenant'       },
  { id: 'actions',      label: 'Ações administrativas' },
  { id: 'provisioning', label: 'Provisionamento'       },
  { id: 'users',        label: 'Usuários e acessos'    },
  { id: 'domains',      label: 'Domínios e tema'       },
  { id: 'logs',         label: 'Logs e auditoria'      },
];

const TenantCockpit = ({ store, onNav, onImpersonate, onSuspend, onChangePlan, onToast, logAction }) => {
  const [activeTab, setActiveTab] = React.useState('overview');

  // Prepara dados do cockpit para este tenant específico (atualiza globals para sub-tabs)
  const cockpitData = React.useMemo(() => {
    return typeof getCockpitDataForStore !== 'undefined'
      ? getCockpitDataForStore(store)
      : COCKPIT_TENANT;
  }, [store && store.id]);

  const tabProps = { cockpitData, onToast, onSwitchTab: setActiveTab, logAction };

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':     return <CockpitOverviewTab  {...tabProps} onImpersonate={() => onImpersonate(store)} onChangePlan={() => onChangePlan(store)} />;
      case 'health':       return <CockpitHealthTab    {...tabProps} />;
      case 'actions':      return <CockpitActionsTab   {...tabProps} onImpersonate={() => onImpersonate(store)} onChangePlan={() => onChangePlan(store)} onSuspend={() => onSuspend(store)} />;
      case 'provisioning': return <CockpitProvisionTab {...tabProps} />;
      case 'users':        return <CockpitUsersTab     {...tabProps} />;
      case 'domains':      return <CockpitDomainsTab   {...tabProps} />;
      case 'logs':         return <CockpitLogsTab      {...tabProps} />;
      default:             return null;
    }
  };

  return (
    <div className="content" style={{ maxWidth: 1200 }}>
      {/* Back */}
      <button className="btn ghost" style={{ marginBottom: 14, padding: '6px 12px', fontSize: 13 }} onClick={() => onNav({ page: 'loja', store })}>
        <Icon name="chevronRight" size={12} style={{ transform: 'rotate(180deg)' }} />
        Voltar para {store.name}
      </button>

      {/* Cockpit header label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Icon name="activity" size={16} style={{ color: 'var(--text-muted)' }} />
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>Tenant Cockpit</span>
      </div>

      {/* Hero header */}
      <TenantCockpitHeader store={store} cockpitData={cockpitData} onImpersonate={() => onImpersonate(store)} onChangePlan={() => onChangePlan(store)} onSuspend={() => onSuspend(store)} onToast={onToast} logAction={logAction} />

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border)', marginBottom: 24, overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 18px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400, color: activeTab === tab.id ? 'var(--text)' : 'var(--text-muted)', cursor: 'pointer', borderBottom: `2px solid ${activeTab === tab.id ? 'var(--brand-navy)' : 'transparent'}`, marginBottom: -2, whiteSpace: 'nowrap', transition: 'color 120ms' }}
            onMouseEnter={e => { if (activeTab !== tab.id) e.currentTarget.style.color = 'var(--text-secondary)'; }}
            onMouseLeave={e => { if (activeTab !== tab.id) e.currentTarget.style.color = 'var(--text-muted)'; }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {renderTab()}
    </div>
  );
};

// ── Tenant Cockpit MVP (dados reais — Fase 0 hardening) ───────────────────────
const CockpitComingSoonCard = ({ title, description }) => (
  <div className="card" style={{ padding: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{title}</h3>
      <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--bg-subtle)', padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase' }}>Em breve</span>
    </div>
    <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>{description}</p>
  </div>
);

const TenantCockpitMvp = ({ store, onNav, onChangePlan, onToast }) => {
  const tenantId = store?.tenant_id ?? store?.id;
  const repairsReady = saIsRealRepairsMode();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [tenant, setTenant] = React.useState(null);
  const [health, setHealth] = React.useState(null);
  const [billing, setBilling] = React.useState(null);
  const [repairs, setRepairs] = React.useState([]);
  const [audit, setAudit] = React.useState([]);

  React.useEffect(() => {
    if (!tenantId) {
      setError('Tenant inválido.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const coreLoads = [
          tenantService.getById(tenantId),
          billingService.getTenantBilling(tenantId),
          auditService.listLogs({ tenant_id: tenantId, page: 1, page_size: 5 }),
        ];
        if (repairsReady) {
          coreLoads.push(repairService.getTenantHealth(tenantId));
          coreLoads.push(repairService.getRepairHistory(tenantId));
        }
        const results = await Promise.all(coreLoads);
        if (cancelled) return;
        const tRes = results[0];
        const b = results[1];
        const a = results[2];
        const h = repairsReady ? results[3] : null;
        const r = repairsReady ? results[4] : [];
        setTenant(tRes.raw || tRes.data);
        setHealth(h);
        setBilling(b);
        setRepairs((r || []).slice(0, 5));
        setAudit(a?.data || []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Erro ao carregar dados do cockpit');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, repairsReady]);

  const commercialLabel = billing?.commercial_status_label || billing?.status || '—';
  const planLabel = tenant?.plan_label || tenant?.plan || health?.plan_label || '—';
  const domain = tenant?.domain || health?.domain || store?.domain || '—';

  if (loading) {
    return (
      <div className="content" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
        Carregando Tenant Cockpit…
      </div>
    );
  }

  if (error) {
    return (
      <div className="content" style={{ maxWidth: 560 }}>
        <button className="btn ghost" style={{ marginBottom: 14 }} onClick={() => onNav({ page: 'lojas' })}>← Voltar para Lojas</button>
        <div className="hint-card" style={{ padding: 16, color: 'var(--danger)' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="content" style={{ maxWidth: 1100 }}>
      <button className="btn ghost" style={{ marginBottom: 14 }} onClick={() => onNav({ page: 'loja', store })}>
        ← Voltar para {tenant?.name || store?.name}
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Icon name="activity" size={16} style={{ color: 'var(--text-muted)' }} />
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>Tenant Cockpit MVP</span>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>{tenant?.name || store?.name}</h1>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          <span>ID: <strong style={{ fontFamily: 'JetBrains Mono, monospace' }}>{tenantId}</strong></span>
          <span>Schema: <strong style={{ fontFamily: 'JetBrains Mono, monospace' }}>{tenant?.schema_name || health?.schema_name}</strong></span>
          <span>Domínio: <strong style={{ fontFamily: 'JetBrains Mono, monospace' }}>{domain}</strong></span>
          <span>Plano: <strong>{planLabel}</strong></span>
          <span>Status comercial: <strong>{commercialLabel}</strong></span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => onNav({ page: 'loja', store })}><Icon name="store" size={14} />Detalhe da loja</button>
          <button className="btn" onClick={() => onNav({ page: 'billing', tenantContext: { tenant_id: tenantId, id: tenantId, name: tenant?.name } })}><Icon name="cardCoin" size={14} />Billing</button>
          <button className="btn" onClick={() => repairsReady
            ? onNav({ page: 'repairs', tenantContext: { tenant_id: tenantId, id: tenantId, name: tenant?.name } })
            : (onToast && onToast('Repairs ainda não disponível — funcionalidade em preparação'))
          }><Icon name="wrench" size={14} />Repairs{!repairsReady && <span style={{ fontSize: 9, fontWeight: 700, marginLeft: 6, color: 'var(--text-muted)' }}>Em breve</span>}</button>
          <button className="btn" onClick={() => onNav({ page: 'auditoria', tenantContext: { tenant_id: tenantId, id: tenantId, name: tenant?.name } })}><Icon name="shield" size={14} />Auditoria</button>
          <button className="btn primary" onClick={() => onChangePlan && onChangePlan(store)}><Icon name="edit" size={14} />Alterar plano</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {repairsReady ? (
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Health checks</h3>
            {(health?.checks || []).length === 0 && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nenhum check disponível.</p>}
            {(health?.checks || []).map(c => (
              <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                <span>{c.label}</span>
                <span style={{ fontWeight: 600, color: c.status === 'ok' ? 'var(--success)' : c.status === 'error' ? 'var(--danger)' : 'var(--warning)' }}>{c.status}</span>
              </div>
            ))}
          </div>
        ) : (
          <CockpitComingSoonCard
            title="Health checks"
            description="Diagnóstico e health do tenant ainda não estão disponíveis no backend. Esta seção será habilitada quando a API de repairs estiver pronta."
          />
        )}
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Fora do MVP</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
            Pedidos, clientes, produtos, storage detalhado, provisionamento visual e impersonação não estão disponíveis neste cockpit MVP.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {repairsReady ? (
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Últimos repairs</h3>
            {repairs.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nenhum repair registrado.</p>}
            {repairs.map(r => (
              <div key={r.id} style={{ fontSize: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600 }}>{r.action_label || r.action}</div>
                <div style={{ color: 'var(--text-muted)' }}>{r.result} · {typeof formatAuditTimestamp === 'function' ? formatAuditTimestamp(r.created_at) : r.created_at}</div>
              </div>
            ))}
          </div>
        ) : (
          <CockpitComingSoonCard
            title="Últimos repairs"
            description="Histórico de repairs e ações corretivas ainda não estão integrados. Nenhuma chamada de API será feita até a Fase de Repairs."
          />
        )}
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Auditoria recente</h3>
          {audit.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nenhum log recente.</p>}
          {audit.map(e => (
            <div key={e.id} style={{ fontSize: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 600 }}>{e.actionLabel || e.action}</div>
              <div style={{ color: 'var(--text-muted)' }}>{e.result} · {e.ts || e.created_at}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Nota: TenantStatusBadge NÃO é exportado aqui — vem de tenant-status.jsx (carrega após)

export {
  TenantCockpit,
  TenantCockpitHeader,
  TenantCockpitMvp,
  _CockpitStatusBadge,
  _renderStatusBadge,
};
